import React from 'react';
import classNames from 'classnames';
import { Icon } from '../../foundations/Icons';
import Button, { LinkButton } from '../Button/Button';

export type Variants = 'info' | 'warning' | 'error' | 'success';
export type ActionItems = React.ReactElement<
  typeof Button | typeof LinkButton
>[];

const JUSTIFY_ALIGNMENT = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
} as const;

export type BannerProps = React.PropsWithChildren<{
  /** Choose from "info", "success", "error" or "warning" */
  variant: Variants;
  /** Title of the banner */
  title?: string;
  /** logo of the banner */
  logo?: string | React.ReactNode;
  /** To set the margin of the banner default is `false` */
  margined?: boolean;
  /** To hide the logo or icon of the banner */
  textOnly?: boolean;
  rounded?: boolean;
  actions?: {
    /** Optional prop used with actions. Defines if the action is aligned at the start, center, or end respectively */
    alignment?: keyof typeof JUSTIFY_ALIGNMENT;
    /** Defines if the action is aligned same line with the content */
    onSameLine?: boolean;
    /** Defines if the action is will be display at bottom of the content */
    sameLineOnMobile?: boolean;
    /** Can be `Button`, `AnchorButton`, `RouterButton` component */
    items?: ActionItems;
    breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  };
  /** linked to `onClick` event of the close icon */
  onClose?: () => void;
  className?: string;
  'data-testid'?: string;
}>;

export const variants = {
  info: {
    icon: {
      name: 'lInputsInfo',
      color: 'text-info-foreground',
    },
    background: 'bg-info-background',
    titleColor: 'text-regular',
  },
  success: {
    icon: {
      name: 'lInputsSuccess',
      color: 'text-success-foreground',
    },
    background: 'bg-success-background',
    titleColor: 'text-regular',
  },
  error: {
    icon: {
      name: 'lInputsError',
      color: 'text-error-foreground',
    },
    background: 'bg-error-background',
    titleColor: 'text-strong',
  },
  warning: {
    icon: {
      name: 'lInputsError',
      color: 'text-warning-foreground',
    },
    background: 'bg-warning-background',
    titleColor: 'text-strong',
  },
} as const;

export const Banner = (props: BannerProps) => {
  const {
    actions,
    children = null,
    variant,
    logo = null,
    margined = false,
    textOnly = false,
    title = '',
    rounded = true,
    onClose = null,
    className,
  } = props;
  const { icon, background, titleColor } = variants[variant];

  const {
    alignment = 'center',
    onSameLine = false,
    sameLineOnMobile = true,
    items = null,
    breakpoint = 'md',
  } = actions || {};

  /**
   * Map of hidden and block classes for each breakpoint.
   * If onSameLine is not set in actions and sameLineOnMobile is true
   * The items will be hidden and show on the desire breakpoint. (default = md)
   */
  const { block, hidden } = {
    hidden: {
      xs: 'xs:hidden',
      md: 'md:hidden',
      lg: 'lg:hidden',
      xl: 'xl:hidden',
      '2xl': '2xl:hidden',
    },
    block: {
      xs: 'xs:block',
      md: 'md:block',
      lg: 'lg:block',
      xl: 'xl:block',
      '2xl': '2xl:block',
    },
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={classNames(
        `border-1 w-full p-s ${background}`,
        {
          'pb-xxxs': !!items?.length && !onSameLine,
          'pb-xs md:pb-s': onSameLine && !sameLineOnMobile,
          rounded,
          'rounded-none': !rounded,
          '!m-0': !margined,
        },
        'm-s sm:m-s md:m-xxl xs:mx-s xs:mb-l',
        className
      )}
    >
      <div
        data-testid="success-banner"
        className="flex flex-row items-start gap-s"
      >
        {!textOnly && (
          <>
            {logo && (
              <>
                {typeof logo === 'string' ? (
                  <div className="hidden grow-0 items-center justify-center xs:flex">
                    <div
                      className="b-2 hidden size-10 rounded border-2 border-background-subtle bg-cover bg-center bg-no-repeat xs:block"
                      style={{
                        backgroundImage: `url(${logo})`,
                      }}
                    />
                  </div>
                ) : (
                  <>{logo}</>
                )}
              </>
            )}
            {!logo && (
              <div className={icon.color}>
                <Icon name={icon.name} inheritFill />
              </div>
            )}
          </>
        )}
        <div className="flex grow flex-row">
          <div className="flex grow flex-col gap-xxxs">
            {title && (
              <div className={`font-body text-label-small-bold ${titleColor}`}>
                {title}
              </div>
            )}
            <div className="whitespace-pre-wrap text-body-small text-regular">
              {children}
            </div>
            {!onSameLine && items && (
              <div className={`flex ${JUSTIFY_ALIGNMENT[alignment]}`}>
                {items}
              </div>
            )}

            {onSameLine && !sameLineOnMobile && (
              <div className={`block ${hidden[breakpoint]}`}>
                <div className={`flex ${JUSTIFY_ALIGNMENT[alignment]}`}>
                  {items}
                </div>
              </div>
            )}
          </div>

          {onSameLine && (
            <div
              className={`flex ${JUSTIFY_ALIGNMENT[alignment]} ${
                !sameLineOnMobile && `hidden ${block[breakpoint]}`
              }`}
            >
              {items}
            </div>
          )}
        </div>

        {onClose && (
          <button className="cursor-pointer" onClick={onClose}>
            <Icon name="mInputsClose" accessibleTitle="close" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Banner;
